import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Membership, User } from "~/types/user";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isObjectID(id: string) {
  return id.length === 24 && /^[0-9a-fA-F]+$/.test(id);
}

// ============================================================================
// Currency Support
// ============================================================================

/**
 * Mapping of currency codes to their symbols
 */
export const CURRENCY_SYMBOLS = {
  USD: "$",
  CAD: "C$",
  AUD: "A$",
  GBP: "£",
  EUR: "€",
} as const;

export type SupportedCurrency = keyof typeof CURRENCY_SYMBOLS;

/**
 * Get currency symbol for a given currency code
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @returns Currency symbol (e.g., "$", "€")
 */
export function getCurrencySymbol(currency: string): string {
  const upperCurrency = currency.toUpperCase() as SupportedCurrency;
  return CURRENCY_SYMBOLS[upperCurrency] || CURRENCY_SYMBOLS.USD;
}

/**
 * Get user's currency from user data, with fallback to USD
 * Handles both User type and any type for backward compatibility
 * @param user - User object that may contain currency field
 * @returns Currency code (e.g., "USD", "EUR")
 */
export function getUserCurrency(user: User | null | undefined): string {
  if (!user) return "USD";
  return user.currency || "USD";
}

/**
 * Format price in cents with currency symbol
 * @param cents - Price in cents
 * @param currency - Currency code (defaults to "USD")
 * @returns Formatted price string (e.g., "$12.34", "€45.67")
 */
export function formatPriceCents(cents: number, currency = "USD"): string {
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

/**
 * Format price in cents with user's currency
 * @param cents - Price in cents
 * @param user - User object that may contain currency field
 * @returns Formatted price string with user's currency symbol
 */
export function formatPriceCentsForUser(cents: number, user: User | null | undefined): string {
  const currency = getUserCurrency(user);
  return formatPriceCents(cents, currency);
}

// ============================================================================
// Region Support
// ============================================================================

/**
 * Mapping of currency codes to their corresponding regions/countries
 */
const CURRENCY_TO_REGION = {
  USD: "United States",
  EUR: "European Union",
  GBP: "United Kingdom",
  CAD: "Canada",
  AUD: "Australia",
  JPY: "Japan",
  CHF: "Switzerland",
  SEK: "Sweden",
  NOK: "Norway",
  DKK: "Denmark",
  PLN: "Poland",
  CZK: "Czech Republic",
  HUF: "Hungary",
  RON: "Romania",
  BGN: "Bulgaria",
  HRK: "Croatia",
  RUB: "Russia",
  CNY: "China",
  INR: "India",
  KRW: "South Korea",
  SGD: "Singapore",
  HKD: "Hong Kong",
  TWD: "Taiwan",
  THB: "Thailand",
  MYR: "Malaysia",
  IDR: "Indonesia",
  PHP: "Philippines",
  VND: "Vietnam",
  BRL: "Brazil",
  MXN: "Mexico",
  ARS: "Argentina",
  CLP: "Chile",
  COP: "Colombia",
  PEN: "Peru",
  ZAR: "South Africa",
  EGP: "Egypt",
  MAD: "Morocco",
  NGN: "Nigeria",
  KES: "Kenya",
  GHS: "Ghana",
  XOF: "West Africa",
  XAF: "Central Africa",
  ILS: "Israel",
  SAR: "Saudi Arabia",
  AED: "United Arab Emirates",
  QAR: "Qatar",
  KWD: "Kuwait",
  BHD: "Bahrain",
  OMR: "Oman",
  JOD: "Jordan",
  LBP: "Lebanon",
  TRY: "Turkey",
  IRR: "Iran",
  PKR: "Pakistan",
  BDT: "Bangladesh",
  LKR: "Sri Lanka",
  NPR: "Nepal",
  BTN: "Bhutan",
  MVR: "Maldives",
  AFN: "Afghanistan",
} as const;

/**
 * Get region/country name from currency code
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @returns Region/country name (e.g., "United States", "European Union")
 */
export function getRegionFromCurrency(currency: string): string {
  const upperCurrency = currency.toUpperCase() as keyof typeof CURRENCY_TO_REGION;
  return CURRENCY_TO_REGION[upperCurrency] || "United States";
}

export function getMembershipTier(membership: Membership): string {
  const tier = membership.tier || "";
  return tier.replace(/_/g, " ");
}

export function getMembershipStatus(status: string): string {
  if (status === "non_member") {
    return "non member";
  }
  if (status === "trial_member") {
    return "trial member";
  }
  if (status === "trial_member_no_renewal") {
    return "trial member";
  }
  if (status === "active_member") {
    return "active member";
  }
  if (status === "active_member_no_renewal") {
    return "active member";
  }
  if (status === "active_member_paused") {
    return "non member";
  }
  return "non member";
}

export function getMembershipStatusShort(status: string): string {
  if (status === "trial_member") {
    return "Trial";
  }
  if (status === "trial_member_no_renewal") {
    return "Trial";
  }
  if (status === "active_member") {
    return "Active";
  }
  if (status === "active_member_no_renewal") {
    return "Active";
  }
  return "Paused";
}
